import React, { useState, useCallback, ReactNode } from 'react';
import { Box, Alert, Button, Typography } from '@mui/material';
import { Refresh as RefreshIcon, Error as ErrorIcon } from '@mui/icons-material';
import { ErrorResponse } from '../../types';

interface RetryWrapperProps {
  children: ReactNode;
  onRetry: () => Promise<void> | void;
  error?: ErrorResponse | null;
  loading?: boolean;
  maxRetries?: number;
  showRetryButton?: boolean;
}

export const RetryWrapper: React.FC<RetryWrapperProps> = ({
  children,
  onRetry,
  error,
  loading = false,
  maxRetries = 3,
  showRetryButton = true
}) => {
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);

  const handleRetry = useCallback(async () => {
    if (retryCount >= maxRetries) {
      return;
    }

    setIsRetrying(true);
    try {
      await onRetry();
      setRetryCount(0); // Reset retry count on success
    } catch (retryError) {
      setRetryCount(prev => prev + 1);
      console.error('Retry failed:', retryError);
    } finally {
      setIsRetrying(false);
    }
  }, [onRetry, retryCount, maxRetries]);

  const getErrorMessage = (error: ErrorResponse): string => {
    // Safe access to error properties with fallbacks
    const errorCode = error?.error?.code;
    const errorMessage = error?.error?.message;
    
    switch (errorCode) {
      case 'NETWORK_ERROR':
        return 'Unable to connect to the server. Please check your internet connection.';
      case 'AUTHENTICATION_ERROR':
        return 'Your session has expired. Please log in again.';
      case 'FORBIDDEN':
        return 'You do not have permission to perform this action.';
      case 'NOT_FOUND':
        return 'The requested resource was not found.';
      case 'VALIDATION_ERROR':
        return 'Please check your input and try again.';
      case 'RATE_LIMITED':
        return 'Too many requests. Please wait a moment before trying again.';
      case 'INTERNAL_SERVER_ERROR':
        return 'A server error occurred. Please try again later.';
      case 'SERVICE_UNAVAILABLE':
        return 'The service is temporarily unavailable. Please try again later.';
      default:
        return errorMessage || 'An unexpected error occurred.';
    }
  };

  const canRetry = error && 
    showRetryButton && 
    retryCount < maxRetries && 
    !loading && 
    !isRetrying &&
    ['NETWORK_ERROR', 'INTERNAL_SERVER_ERROR', 'SERVICE_UNAVAILABLE', 'GATEWAY_TIMEOUT'].includes(error?.error?.code || '');

  if (error) {
    return (
      <Box p={2}>
        <Alert 
          severity="error" 
          action={
            canRetry ? (
              <Button
                color="inherit"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={handleRetry}
                disabled={isRetrying}
              >
                {isRetrying ? 'Retrying...' : `Retry (${maxRetries - retryCount} left)`}
              </Button>
            ) : undefined
          }
        >
          <Box display="flex" alignItems="center" gap={1}>
            <ErrorIcon fontSize="small" />
            <Typography variant="body2">
              {getErrorMessage(error)}
            </Typography>
          </Box>
          
          {retryCount >= maxRetries && (
            <Typography variant="caption" display="block" sx={{ mt: 1, opacity: 0.8 }}>
              Maximum retry attempts reached. Please refresh the page or contact support.
            </Typography>
          )}
        </Alert>
      </Box>
    );
  }

  return <>{children}</>;
};

export default RetryWrapper;